import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { userAtom, notificationsAtom, tokenAtom } from '../../../atoms/Atoms';
import { H2 } from '../../shared-components/typography/Title';
import AuthLayout from '../../shared-components/auth-layout/AuthLayout';
import Button from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import Input from '../../shared-components/form/Input';
import api from '../../../api/api';
import { encryptString } from '../../../functions';
import envConfig from '../../../envConfig';

function Login() {
    const [notifications, setNotifications] = useRecoilState(notificationsAtom);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [user, setUser] = useRecoilState(userAtom);
    const setToken = useSetRecoilState(tokenAtom);
    const [loading, setLoading] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);

    useEffect(() => {
      if (!loggingIn && user) {
        window.location = '/home';
      }
    }, [user]);

    function login(values) {
      setLoading(true);
      const payload = {
        Username: values.email,
        Password: encryptString(values.password)
      };
      api.post('auth/login', payload)
        .then((response) => {
          setLoading(false);
          if (response.data.success) {
            if (!response.data.data.profile.acceptedOn) {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: t('your_account_not_approved_yet'),
                  error: true,
                },
              ]);
            } else if (response.data.data.profile.isBlocked) {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: t('your_account_is_blocked'),
                  error: true,
                },
              ]);
            } else {
              setLoggingIn(true);
              localStorage.setItem(
                `${envConfig.StoragePrefix}-user`,
                JSON.stringify(response.data.data.profile)
              );
              localStorage.setItem(
                `${envConfig.StoragePrefix}-token`,
                response.data.data.accessToken
              );
              localStorage.setItem(
                `${envConfig.StoragePrefix}-token-date`,
                dayjs().toString()
              );
              setToken(response.data.data.accessToken);
              return response;
            }
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response.data.error,
                error: true,
              },
            ]);
          }
        })
        .then((response) => {
          if (response) {
            setUser(response.data.data.profile);
            if (response.data.data.profile.forcePasswordReset) {
              navigate('/updatepass');
            } else {
              navigate('/home');
            }
          }
        })
        .catch((error) => {
          setLoading(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
    }

    const formik = useFormik({
      initialValues: {
        email: '',
        password: ''
      },
      validationSchema: Yup.object().shape({
        email: Yup.string()
          .email(t('valid_email_validation'))
          .required(t('required_field')),
        password: Yup.string().required(t('required_field')),
      }),
      onSubmit: (values, { setSubmitting }) => login(values),
    });

    return (
      <AuthLayout title={`${t('welcome_message')} - ${envConfig.SiteTitle}`}>
            <H2 className="text-ink" text={t('login_to_interact')} />
            <p className="mt-2 text-sm text-muted lg:hidden">
              {`${t('welcome_message')} - ${envConfig.SiteTitle}`}
            </p>
            <Form className="mt-7 space-y-7">
              <Input
                label={t('email')}
                required
                type="email"
                name="email"
                id="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={
                  formik.errors.email && formik.touched.email && formik.errors.email
                }
              />
              <Input
                label={t('password')}
                required
                id="password"
                name="password"
                type="password"
                autoComplete="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.errors.password &&
                  formik.touched.password &&
                  formik.errors.password
                }
              />
            </Form>
            <div className="mt-8 space-y-3">
              <Button
                className="w-full bg-primaryColor"
                textColor="text-primaryTextColor"
                loaderColor="bg-primaryTextColor"
                borderColor="border-primaryBorderColor"
                loading={loading}
                text={t('sign_in')}
                onClick={formik.submitForm}
              />
              <Button
                disabled={loading}
                className="w-full bg-surface"
                textColor="text-accentColor"
                borderColor="border-accentBorderColor"
                text={t('create_account')}
                onClick={() => navigate('/register')}
              />
              <Button
                disabled={loading}
                className="w-full bg-transparent shadow-none hover:shadow-none"
                textColor="text-muted"
                text={t('forgot_password')}
                icon={faLockOpen}
                onClick={() => navigate('/recover')}
              />
            </div>
      </AuthLayout>
    );
}

export default Login;
